import React from 'react';
import styled from 'styled-components';
import { Button, Icon, Modal, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import ModalBody from './ModalBody';
import { BaseModalProps, DEFAULT_MODAL_HEIGHT, DEFAULT_MODAL_WIDTH } from './model';

export const DEFAULT_CONFIRM_LABEL = 'Export';
export const DEFAULT_CANCEL_LABEL = 'Cancel';

export interface ConfirmCancelModalProps extends BaseModalProps {
  confirmLabel?: string;
  cancelLabel?: string;
  hasConfirmButton?: boolean;
  hasCancelButton?: boolean;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  hasTitleBar?: boolean;
  hasFooterBar?: boolean;
  hasButtons?: boolean;
  closeOnConfirm?: boolean;
  closeOnCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface ModalContentProps {
  width: string;
  height: string;
  hasTitleBar: boolean;
  hasFooterBar: boolean;
}

const useStyles = makeStyles(() => ({
  modalContainer: {
    overflow: 'scroll',
  },
}));

const ModalContent = styled.div<ModalContentProps>`
  position: absolute;
  width: ${(props) => props.width};
  minheight: ${(props) => props.height};
  border-radius: 2px;
  background-color: #fff;
  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.5);
  margin-bottom: 150px;

  ${(props) => `
    left: calc( (100vw - ${props.width}) / 2 );
    top: 118px;
  `};

  @media (max-width: 960px) {
    width: 100%;
    left: 0;
  }

  display: flex;
  flex-direction: column;

  .modal-body {
    overflow: auto;
  }

  ${(props) =>
    !props.hasTitleBar &&
    `.modal-header {
      border-bottom: none;
    }`}

  ${(props) =>
    !props.hasFooterBar &&
    `.modal-body {
      text-align: center;
      padding-top: 0;
    }

    .buttons {
      text-align: center;

      button {
        width: 120px;
        margin: 7px;
      }
    }`}
`;

const ConfirmCancelModal: React.FC<ConfirmCancelModalProps> = (props: ConfirmCancelModalProps) => {
  const {
    isOpen,
    title,
    hasTitleBar = true,
    hasFooterBar = true,
    hasButtons = false,
    confirmLabel = DEFAULT_CONFIRM_LABEL,
    cancelLabel = DEFAULT_CANCEL_LABEL,
    hasConfirmButton = true,
    hasCancelButton = true,
    confirmDisabled = false,
    cancelDisabled = false,
    width = DEFAULT_MODAL_WIDTH,
    height = DEFAULT_MODAL_HEIGHT,
    closeOnConfirm = false,
    closeOnCancel = true,
    openStatusChanged = () => null,
    onConfirm = () => null,
    onCancel = () => null,
    children,
  } = props;

  const classes = useStyles();

  const handleClose = () => {
    openStatusChanged(false);
  };

  const headerCloseButton = (
    <Icon fontSize="large" onClick={onCancel} style={{ cursor: 'pointer' }}>
      <CloseIcon />
    </Icon>
  );

  const confirmButton = hasConfirmButton ? (
    <Button
      variant="contained"
      disabled={confirmDisabled}
      onClick={() => {
        onConfirm();
        if (closeOnConfirm) handleClose();
      }}
    >
      {confirmLabel}
    </Button>
  ) : null;

  const cancelButton = hasCancelButton ? (
    <Button
      variant="outlined"
      disabled={cancelDisabled}
      onClick={() => {
        onCancel();
        if (closeOnCancel) handleClose();
      }}
    >
      {cancelLabel}
    </Button>
  ) : null;

  return (
    <Modal open={isOpen} onClose={handleClose} title={title} className={classes.modalContainer}>
      <ModalContent width={width} height={height} hasTitleBar={hasTitleBar} hasFooterBar={hasFooterBar}>
        <ModalHeader className="modal-header">
          {hasTitleBar && (
            <Typography className="title" variant="h2">
              {title}
            </Typography>
          )}
          {headerCloseButton}
        </ModalHeader>

        <ModalBody className="modal-body">
          {children}
          {!hasFooterBar && (
            <div className="buttons">
              {cancelButton}
              {confirmButton}
            </div>
          )}
        </ModalBody>

        {hasFooterBar && !hasButtons && (
          <ModalFooter>
            <div className="buttons">
              {cancelButton}
              {confirmButton}
            </div>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ConfirmCancelModal;
